import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './CompanyInfoModal.scss';
import { ProfileEditModal } from './ProfileEditModal';
import { validateCompanyInfo } from './validation';
import { locationData, Country, Province } from '../../utils/locationData';

interface CompanyInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: Record<string, any>) => Promise<void>;
  initialData: {
    companyName: string;
    professionalPhone: string;
    address: string;
    unit: string;
    postalCode: string;
    city: string;
    province: string;
    companyCountry: string;
  };
}

export const CompanyInfoModal: React.FC<CompanyInfoModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialData,
}) => {
  const { t } = useTranslation('profile');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format based on length
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = formatPhoneNumber(e.target.value);
  };

  const handleSubmit = async (data: Record<string, any>) => {
    setIsSubmitting(true);
    try {
      await onSuccess(data);
      onClose();
    } catch (error) {
      console.error('Error updating company info:', error);
    }
    setIsSubmitting(false);
  };

  return (
    <ProfileEditModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={t('sections.company.title').toUpperCase()}
      validate={validateCompanyInfo}
      isSubmitting={isSubmitting}
    >
      <div className="form-groups-grid-company">
        <div className="form-group">
          <label htmlFor="companyName">{t('sections.company.companyName')}</label>
          <input
            type="text"
            id="companyName"
            name="companyName"
            defaultValue={initialData.companyName}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="professionalPhone">{t('sections.company.professionalPhone')}*</label>
          <input
            type="tel"
            id="professionalPhone"
            name="professionalPhone"
            defaultValue={formatPhoneNumber(initialData.professionalPhone || '')}
            onChange={handlePhoneChange}
            placeholder="(xxx) xxx-xxxx"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="address">{t('sections.company.address')}</label>
          <input
            type="text"
            id="address"
            name="address"
            defaultValue={initialData.address}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="unit">{t('sections.company.unit')}</label>
          <input
            type="text"
            id="unit"
            name="unit"
            defaultValue={initialData.unit}
          />
        </div>

        <div className="form-group">
          <label htmlFor="postalCode">{t('sections.company.postalCode')}</label>
          <input
            type="text"
            id="postalCode"
            name="postalCode"
            defaultValue={initialData.postalCode}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="city">{t('sections.company.city')}</label>
          <input
            type="text"
            id="city"
            name="city"
            defaultValue={initialData.city}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="province">{t('sections.company.province')}</label>
          <div className="select-wrapper">
            <select
              id="province"
              name="province"
              defaultValue={initialData.province}
              required
            >
              <option value="">{t('sections.company.selectProvince')}</option>
              {locationData
                .find((c: Country) => c.code === 'CA')?.provinces
                .map((province: Province) => (
                  <option key={province.code} value={province.code}>
                    {t(province.translationKey)}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="companyCountry">{t('sections.company.country')}</label>
          <input
            type="text"
            id="companyCountry"
            name="companyCountry"
            value="Canada"
            readOnly
          />
          <input
            type="hidden"
            name="companyCountry"
            value="CA"
          />
        </div>
      </div>
    </ProfileEditModal>
  );
};
